import React from 'react';
import {
    Users, CheckCircle, Clock, Briefcase, BookOpen, Building2,
    GraduationCap, TrendingUp, PieChart, Activity, Coins, Settings,
    FileText, RefreshCw
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { TabsContent } from '../../../components/ui/tabs';

const OverviewTab = ({ analytics, growthData, pendingItems, getItemIcon, getItemTitle, handleReview, handleRefresh }) => (
    <TabsContent value="overview" className="space-y-6">
        <div className="grid lg:grid-cols-3 gap-6">
            {/* User Distribution */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center">
                        <PieChart className="h-5 w-5 mr-2 text-blue-600" />
                        User Distribution
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <div className="w-3 h-3 rounded-full bg-green-500 mr-2" />
                                <span className="text-sm">Students</span>
                            </div>
                            <span className="font-semibold">{analytics?.users?.students || 0}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <div className="w-3 h-3 rounded-full bg-purple-500 mr-2" />
                                <span className="text-sm">Tutors</span>
                            </div>
                            <span className="font-semibold">{analytics?.users?.tutors || 0}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <div className="w-3 h-3 rounded-full bg-orange-500 mr-2" />
                                <span className="text-sm">Organizations</span>
                            </div>
                            <span className="font-semibold">{analytics?.users?.organizations || 0}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <div className="w-3 h-3 rounded-full bg-red-500 mr-2" />
                                <span className="text-sm">Admins</span>
                            </div>
                            <span className="font-semibold">1</span>
                        </div>
                    </div>

                    {/* Visual Bar */}
                    <div className="mt-4 h-4 rounded-full overflow-hidden bg-gray-100 flex">
                        <div className="bg-green-500" style={{ width: `${(analytics?.users?.students / analytics?.users?.total) * 100 || 0}%` }} />
                        <div className="bg-purple-500" style={{ width: `${(analytics?.users?.tutors / analytics?.users?.total) * 100 || 0}%` }} />
                        <div className="bg-orange-500" style={{ width: `${(analytics?.users?.organizations / analytics?.users?.total) * 100 || 0}%` }} />
                    </div>
                </CardContent>
            </Card>

            {/* User Growth */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center">
                        <TrendingUp className="h-5 w-5 mr-2 text-green-600" />
                        User Growth (14 days)
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex items-end justify-between h-40 gap-1 px-1">
                        {growthData.slice(-14).map((day, index) => {
                            const maxCount = Math.max(...growthData.slice(-14).map(d => d.count), 1);
                            const barHeight = maxCount > 0 ? Math.max(6, Math.round((day.count / maxCount) * 110)) : 6;
                            return (
                                <div key={index} className="flex flex-col items-center flex-1 min-w-0">
                                    <span className="text-[9px] text-gray-500 mb-1 font-medium">{day.count}</span>
                                    <div
                                        className="w-full max-w-[22px] bg-gradient-to-t from-blue-600 to-blue-400 rounded-t-sm transition-all hover:from-blue-700 hover:to-blue-500 cursor-pointer"
                                        style={{ height: `${barHeight}px` }}
                                        title={`${day.date}: ${day.count} users`}
                                    />
                                    <span className="text-[10px] text-gray-400 mt-1">
                                        {new Date(day.date).getDate()}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-4 text-center">
                        <p className="text-sm text-gray-500">
                            <span className="font-semibold text-green-600">{analytics?.users?.recent_7_days || 0}</span> new users this week
                        </p>
                    </div>
                </CardContent>
            </Card>

            {/* Platform Activity */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center">
                        <Activity className="h-5 w-5 mr-2 text-purple-600" />
                        Platform Activity
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                            <div className="flex items-center">
                                <Briefcase className="h-5 w-5 text-blue-600 mr-2" />
                                <span className="text-sm">Total Jobs</span>
                            </div>
                            <span className="font-bold text-blue-600">{analytics?.content?.jobs || 0}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                            <div className="flex items-center">
                                <BookOpen className="h-5 w-5 text-green-600 mr-2" />
                                <span className="text-sm">Tuition Posts</span>
                            </div>
                            <span className="font-bold text-green-600">{analytics?.content?.tuition_posts?.total || 0}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                            <div className="flex items-center">
                                <Users className="h-5 w-5 text-purple-600 mr-2" />
                                <span className="text-sm">Total Bookings</span>
                            </div>
                            <span className="font-bold text-purple-600">{analytics?.engagement?.bookings?.total || 0}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                            <div className="flex items-center">
                                <Coins className="h-5 w-5 text-yellow-600 mr-2" />
                                <span className="text-sm">Coins Spent</span>
                            </div>
                            <span className="font-bold text-yellow-600">{analytics?.revenue?.estimated_coins_spent || 0}</span>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>

        {/* Recent Activity & Quick Actions */}
        <div className="grid lg:grid-cols-2 gap-6">
            {/* Pending Items Preview */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg flex items-center">
                        <Clock className="h-5 w-5 mr-2 text-yellow-600" />
                        Pending Reviews
                    </CardTitle>
                    <Button variant="outline" size="sm" onClick={() => document.querySelector('[value="reviews"]')?.click()}>
                        View All
                    </Button>
                </CardHeader>
                <CardContent>
                    {pendingItems.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-400" />
                            <p>All caught up! No pending reviews.</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {pendingItems.slice(0, 5).map((item) => (
                                <div key={`${item.type}-${item.id}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center space-x-3">
                                        <div className={`p-2 rounded-lg ${item.type === 'job' ? 'bg-blue-100' :
                                            item.type === 'tuition' ? 'bg-green-100' :
                                                item.type === 'tutor' ? 'bg-purple-100' : 'bg-orange-100'
                                            }`}>
                                            {getItemIcon(item.type)}
                                        </div>
                                        <div>
                                            <p className="font-medium text-sm">{getItemTitle(item)}</p>
                                            <p className="text-xs text-gray-500 capitalize">{item.type}</p>
                                        </div>
                                    </div>
                                    <Button size="sm" variant="outline" onClick={() => handleReview(item, item.type)}>
                                        Review
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center">
                        <Settings className="h-5 w-5 mr-2 text-gray-600" />
                        Quick Actions
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                        <Button variant="outline" className="h-20 flex flex-col items-center justify-center space-y-2" onClick={() => document.querySelector('[value="users"]')?.click()}>
                            <Users className="h-6 w-6 text-blue-600" />
                            <span className="text-sm">Manage Users</span>
                        </Button>
                        <Button variant="outline" className="h-20 flex flex-col items-center justify-center space-y-2" onClick={() => document.querySelector('[value="reviews"]')?.click()}>
                            <CheckCircle className="h-6 w-6 text-green-600" />
                            <span className="text-sm">Review Content</span>
                        </Button>
                        <Button variant="outline" className="h-20 flex flex-col items-center justify-center space-y-2" onClick={() => document.querySelector('[value="content"]')?.click()}>
                            <FileText className="h-6 w-6 text-purple-600" />
                            <span className="text-sm">All Content</span>
                        </Button>
                        <Button variant="outline" className="h-20 flex flex-col items-center justify-center space-y-2" onClick={handleRefresh}>
                            <RefreshCw className="h-6 w-6 text-orange-600" />
                            <span className="text-sm">Refresh Data</span>
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    </TabsContent>
);

export default OverviewTab;
